import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { Card, SearchBar } from '../ui';
import LeaveRequestCard from './LeaveRequestCard';
import leaveApi from './leaveApi';

const LeaveRequests = () => {
  const location = useLocation();
  const { openOverlay } = useOverlay();
  
  // Determine active tab from route
  const getActiveTabFromRoute = () => {
    if (location.pathname.includes('applied')) return 'applied';
    if (location.pathname.includes('history')) return 'history';
    if (location.pathname.includes('team')) return 'team';
    return 'applied'; // default
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  
  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  // Data states
  const [appliedRequests, setAppliedRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);

  // Pagination states for team requests
  const [teamRequestsPagination, setTeamRequestsPagination] = useState({
    hasMore: false,
    lastEvaluatedKey: null,
    isLoadingMore: false,
  });

  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [hasUserSelectedMonth, setHasUserSelectedMonth] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    setError('');

    try {
      switch (activeTab) {
        case 'applied':
          await fetchAppliedRequests();
          break;
        case 'history':
          await fetchHistoryRequests();
          break;
        case 'team':
          await fetchTeamRequests();
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${activeTab} requests:`, error);
      setError(error.message || `Failed to load ${activeTab} requests`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAppliedRequests = async () => {
    try {
      const response = await leaveApi.getMyLeaveRequests();
      setAppliedRequests(response.leave_requests || []);
      
      // Log successful API call
      loggingService.logInfo(
        'leaveFetchAppliedRequests',
        'Successfully fetched applied leave requests',
        {
          endpoint: '/leave',
          method: 'GET',
          requestCount: response.leave_requests?.length || 0,
        }
      ).catch(logError => {
        console.warn('[LeaveRequests] Failed to log event:', logError);
      });
    } catch (error) {
      // Error logging is handled in leaveApi.js
      throw error;
    }
  };

  const fetchHistoryRequests = async () => {
    try {
      const response = await leaveApi.getDecidedLeaveRequests();
      setHistoryRequests(response.leave_requests || []);
      
      // Log successful API call
      loggingService.logInfo(
        'leaveFetchHistoryRequests',
        'Successfully fetched history leave requests',
        {
          endpoint: '/leave/manager/not-pending',
          method: 'GET',
          requestCount: response.leave_requests?.length || 0,
        }
      ).catch(logError => {
        console.warn('[LeaveRequests] Failed to log event:', logError);
      });
    } catch (error) {
      // Error logging is handled in leaveApi.js
      throw error;
    }
  };

  const fetchTeamRequests = async (exclusiveStartKey = null) => {
    try {
      const response = await leaveApi.getPendingLeaveRequests(
        50,
        exclusiveStartKey
      );
      const newRequests = response.leave_requests || [];

      if (exclusiveStartKey) {
        // Append to existing requests for pagination
        setTeamRequests(prev => [...prev, ...newRequests]);
      } else {
        // Replace requests for initial load
        setTeamRequests(newRequests);
      }

      // Update pagination state
      setTeamRequestsPagination({
        hasMore: response.has_more || false,
        lastEvaluatedKey: response.last_evaluated_key || null,
        isLoadingMore: false,
      });
      
      // Log successful API call
      loggingService.logInfo(
        'leaveFetchTeamRequests',
        'Successfully fetched team leave requests',
        {
          endpoint: '/leave/manager/pending',
          method: 'GET',
          requestCount: newRequests.length,
          hasMore: response.has_more || false,
          isPagination: !!exclusiveStartKey,
        }
      ).catch(logError => {
        console.warn('[LeaveRequests] Failed to log event:', logError);
      });
    } catch (error) {
      console.error('Error fetching team requests:', error);
      setError(error.message || 'Failed to load team requests');
      // Error logging is handled in leaveApi.js
    }
  };

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate search suggestions based on available data
    if (value.length > 0) {
      const allRequests = [
        ...appliedRequests,
        ...historyRequests,
        ...teamRequests,
      ];
      const suggestions = new Set();

      allRequests.forEach(request => {
        if (request.leave_type?.toLowerCase().includes(value.toLowerCase())) {
          suggestions.add(request.leave_type);
        }
        if (request.reason?.toLowerCase().includes(value.toLowerCase())) {
          const words = request.reason
            .split(' ')
            .filter(
              word =>
                word.toLowerCase().includes(value.toLowerCase()) &&
                word.length > 2
            );
          words.forEach(word => suggestions.add(word));
        }
        if (request.raised_by?.toLowerCase().includes(value.toLowerCase())) {
          suggestions.add(request.raised_by);
        }
      });

      setSearchSuggestions(Array.from(suggestions).slice(0, 10));
    } else {
      setSearchSuggestions([]);
    }
  };

  const handleSearchSubmit = value => {
    console.log('Search submitted:', value);
    // Add your search logic here
  };

  const handleSuggestionClick = suggestion => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    // You can add logic here to filter or navigate based on the suggestion
  };

  const handleRequestAction = (request, action) => {
    openOverlay('leaveRequestDialog', {
      request: request,
      onApprove: handleApprove,
      onReject: handleReject
    });
  };

  const handleLoadMoreTeamRequests = async () => {
    if (teamRequestsPagination.isLoadingMore || !teamRequestsPagination.hasMore)
      return;

    setTeamRequestsPagination(prev => ({ ...prev, isLoadingMore: true }));
    await fetchTeamRequests(teamRequestsPagination.lastEvaluatedKey);
  };

  const handleApprove = async request => {
    await fetchTeamRequests(); // Refresh team requests
  };

  const handleReject = async request => {
    await fetchTeamRequests(); // Refresh team requests
  };

  const handleMonthSelect = month => {
    setSelectedMonth(month);
    setHasUserSelectedMonth(true);
  };

  const clearMonthFilter = () => {
    setSelectedMonth(null);
    setHasUserSelectedMonth(false);
  };

  const filterRequestsByMonth = requests => {
    if (!hasUserSelectedMonth || !selectedMonth) return requests;

    return requests.filter(request => {
      if (!request.date_list || request.date_list.length === 0) return false;
      const firstDate = new Date(request.date_list[0]);
      return (
        firstDate.getMonth() === selectedMonth.getMonth() &&
        firstDate.getFullYear() === selectedMonth.getFullYear()
      );
    });
  };

  const filterRequestsBySearch = requests => {
    if (!searchTerm.trim()) return requests;

    const searchLower = searchTerm.toLowerCase();
    return requests.filter(request => {
      return (
        request.leave_type?.toLowerCase().includes(searchLower) ||
        request.reason?.toLowerCase().includes(searchLower) ||
        request.raised_by?.toLowerCase().includes(searchLower) ||
        request.decision_by?.toLowerCase().includes(searchLower) ||
        (request.date_list &&
          request.date_list.some(date =>
            new Date(date)
              .toLocaleDateString()
              .toLowerCase()
              .includes(searchLower)
          ))
      );
    });
  };

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">❌ {error}</div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      );
    }

    let requests = [];
    let emptyMessage = '';

    switch (activeTab) {
      case 'applied':
        requests = filterRequestsBySearch(
          filterRequestsByMonth(appliedRequests)
        );
        emptyMessage = 'No leave requests found';
        break;
      case 'history':
        requests = filterRequestsBySearch(
          filterRequestsByMonth(historyRequests)
        );
        emptyMessage = 'No leave history found';
        break;
      case 'team':
        requests = filterRequestsBySearch(teamRequests);
        emptyMessage = 'No pending team requests found';
        break;
    }

    if (requests.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-2">📋</div>
          <div className="text-gray-600">{emptyMessage}</div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {requests.map((request, index) => (
          <LeaveRequestCard
            key={request.request_id || index}
            request={request}
            type={activeTab}
            canApprove={activeTab === 'team' ? false : true}
            onApprove={() => handleRequestAction(request, 'approve')}
            onReject={() => handleRequestAction(request, 'reject')}
            onViewDetails={() => handleRequestAction(request, 'view')}
          />
        ))}

        {/* Load More button for team requests */}
        {activeTab === 'team' && teamRequestsPagination.hasMore && (
          <div className="text-center mt-6">
            <button
              onClick={handleLoadMoreTeamRequests}
              disabled={teamRequestsPagination.isLoadingMore}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {teamRequestsPagination.isLoadingMore
                ? 'Loading...'
                : 'Load More Requests'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMonthFilter = () => {
    if (activeTab === 'team') return null; // No month filter for team requests

    return (
      <Card.Gradient className="p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h6
              className={typography.h6}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              Filter by Month
            </h6>
            <p className={typography.p} style={{ color: '#666666' }}>
              {hasUserSelectedMonth
                ? `Filtered by: ${selectedMonth?.toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}`
                : 'Select a month to filter requests'}
            </p>
          </div>
          <div className="flex gap-2">
            {hasUserSelectedMonth && (
              <button
                onClick={clearMonthFilter}
                className="px-3 py-2 text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            )}
            <input
              type="month"
              value={
                selectedMonth
                  ? `${selectedMonth.getFullYear()}-${String(
                      selectedMonth.getMonth() + 1
                    ).padStart(2, '0')}`
                  : ''
              }
              onChange={e => {
                if (e.target.value) {
                  const [year, month] = e.target.value.split('-');
                  handleMonthSelect(new Date(year, month - 1));
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </Card.Gradient>
    );
  };

  return (
    <div className="leave-requests-container">
      {/* Header */}
      <div className="mb-8 text-center">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Leave Requests
        </h3>
        <p
          className={typography.p}
          style={{ color: '#666666', marginTop: '0.5rem' }}
        >
          Manage your leave requests and team approvals
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <SearchBar
          placeholder="Search leave requests, employees, or dates..."
          value={searchTerm}
          onChange={handleSearchChange}
          onSearch={handleSearchSubmit}
          suggestions={searchSuggestions}
          onSuggestionClick={handleSuggestionClick}
          className="w-full max-w-2xl mx-auto"
        />
      </div>


      {/* Month Filter */}
      {renderMonthFilter()}

      {/* Refresh Button */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={fetchData}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <svg
            className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">{renderTabContent()}</div>

      <style>{`
        .leave-requests-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0rem;
        }
      `}</style>
    </div>
  );
};

export default LeaveRequests;